import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Button } from '@bettercomp/ui-components';
import { selectPolicyManagerNotifications } from 'redux/notifications/selectors';

import { showNotification } from '../common/function';
import { PATH } from '../common/constants';
import { PricingPolicyService } from "../types";

export function usePolicyManagerCompleteEvent() {
  const router = useRouter();
  const notifications = useSelector(selectPolicyManagerNotifications);
  const previousNotificationsRef = useRef(notifications);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLoadingRef = useRef(false);

  const notificationBody = useMemo(() => (
    <div>
      The update has finished. View the details and history in the
      <Button
        type="link"
        onClick={() => router.push(PATH.TABLE_PRICEJOB_MASS_POLICY_HISTORY)}
        size="small"
        data-testid="policyHistory"
      >
        Policy Manager
      </Button>
    </div>
  ), [router]);

  const checkPolicyRunCompletion = useCallback((policyHistoryId: number): void => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(async () => {
      if (isLoadingRef.current) {
        return;
      }
      isLoadingRef.current = true;

      try {
        const { isComplete } = await PricingPolicyService.getPricingPolicyHistoryStatus({
          id: policyHistoryId
        });

        if (isComplete) {
          showNotification(notificationBody as any, 'Policy Run Complete', 'success');
        }
      } catch (err) {
        console.error('Failed to check policy run completion status', err);
      } finally {
        isLoadingRef.current = false;
      }
    }, 3000);
  }, [notificationBody]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      isLoadingRef.current = false;
    };
  }, []);

  useEffect(() => {
    const previousNotifications = previousNotificationsRef.current;

    if (previousNotifications && previousNotifications.length > notifications.length && notifications.length === 0) {
      const historyId = previousNotifications[0]?.details?.pricingPolicyApplyHistoryId;
      if (historyId !== undefined) {
        checkPolicyRunCompletion(historyId);
      }
    }

    previousNotificationsRef.current = notifications;
  }, [notifications, checkPolicyRunCompletion]);
}
